/**
 * Custom Element for Infinite Scrolling Sections.
 *
 * This component fetches the next page of content via the Shopify sections API,
 * parses the HTML, and appends the new product grid items. It uses
 * IntersectionObserver for triggering and pre-fetches the next page's
 * content immediately after a successful load to minimize perceived latency.
 */
class InfiniteScrollSections extends HTMLElement {
  constructor() {
    super();

    this.canonical_link = document.querySelector('[rel="canonical"]');
    this.fetchCount = 1;

    // 1. Configuration from data attributes
    this.sectionId = this.dataset.sectionId;
    this.productGridSelector = this.dataset.productGridSelector;
    this.paginationSelector = this.dataset.paginationSelector;
    this.nextButtonSelector = '.pagination__item--next';

    // 2. DOM references
    this.productGrid = document.querySelector(this.productGridSelector);
    this.pagination = document.querySelector(this.paginationSelector);
    this.sentinel = this; // The element itself acts as the trigger point

    // 3. State management
    this.state = {
      isLoading: false, // Prevents multiple simultaneous loads
      hasNextPage: true, // Used to hide the sentinel when done
    };
    this.cache = {}; // Stores promises and fetched DOM content
    this.nextPageUrl = this._getInitialNextUrl();

    // 4. Loading indicator reference
    this.loadingIndicator = this._createLoadingIndicator();
  }

  connectedCallback() {
    // Only proceed if we have a grid to append to and a starting URL.
    if (this.nextPageUrl && this.productGrid && this.sectionId) {
      this._initIntersectionObserver();
      this.productGrid.after(this.loadingIndicator); // Place indicator after the grid
      // Start fetching the first "next" page immediately (prefetch)
      this._prefetchNextPage(this.nextPageUrl);
    } else {
      this.style.display = 'none';
      this.state.hasNextPage = false;
    }
  }

  // --- Utility Methods ---

  _getInitialNextUrl() {
    return this.pagination?.querySelector(this.nextButtonSelector)?.href;
  }

  _createLoadingIndicator() {
    const indicator = document.createElement('div');
    indicator.className = 'infinite-scroll-loading-indicator visually-hidden';
    indicator.innerHTML = `
      <div class="spinner"></div>
      <p>Loading more products...</p>
    `;
    return indicator;
  }

  _showLoading() {
    this.state.isLoading = true;
    this.loadingIndicator.classList.remove('visually-hidden');
  }

  _hideLoading() {
    this.state.isLoading = false;
    this.loadingIndicator.classList.add('visually-hidden');
  }

  // --- Observer Initialization ---

  _initIntersectionObserver() {
    const options = {
      // Trigger when the element is 50px away from the viewport bottom
      rootMargin: '250px 0px',
      threshold: 0,
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (
          entry.isIntersecting &&
          !this.state.isLoading &&
          this.state.hasNextPage
        ) {
          this._loadNextPage();
        }
      });
    }, options);

    observer.observe(this.sentinel);
  }

  // --- Prefetching Logic (Robust and Async) ---

  async _fetchPage(url) {
    if (!url) {
      return null;
    }

    // NEW: Increment count and capture the value for this specific batch
    this.fetchCount++;
    const currentBatchId = this.fetchCount;

    // Use the URL as the key for caching
    const sectionUrl = `${url}&sections=${this.sectionId}`;

    try {
      const response = await fetch(sectionUrl);
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      const data = await response.json();
      const sectionHtml = data[this.sectionId];

      if (!sectionHtml) {
        throw new Error(
          'Section HTML not found in API response for pre-fetch.',
        );
      }

      // Parsing HTML *once* during pre-fetch to avoid synchronous parsing on scroll
      const parser = new DOMParser();
      const html = parser.parseFromString(sectionHtml, 'text/html');

      // Extract the content and next URL
      const content = html.querySelector(this.productGridSelector);
      const pagination = html.querySelector(this.paginationSelector);
      const nextUrl = pagination?.querySelector(this.nextButtonSelector)?.href;

      // NEW: Inject the CSS variable into the new grid items
      if (content) {
        // Find all direct children grid items (adjust selector if nested deeper)
        const gridItems = content.querySelectorAll('.grid__item');

        gridItems.forEach((item) => {
          // Sets style="--fetch-count: 1;" (or 2, 3, etc.)
          item.style.setProperty('--fetch-count', currentBatchId);
        });
      }

      return { content, pagination, nextUrl };
    } catch (error) {
      console.error(`Failed to fetch page: ${url}`, error);
      // Mark as failed in cache, but don't re-throw to allow component to continue
      return { error: true };
    }
  }

  _prefetchNextPage(url) {
    if (!url || this.cache[url]) {
      return;
    }

    // Store the Promise in the cache
    const fetchPromise = this._fetchPage(url);
    this.cache[url] = fetchPromise;
  }

  // --- Loading/Rendering Logic (Smooth UX) ---

  async _loadNextPage() {
    this._showLoading();

    // Wait for the content to be ready from the pre-fetch cache.
    const cacheEntry = await this.cache[this.nextPageUrl];

    // Guard against cases where the pre-fetch might not have run for the current URL.
    if (!cacheEntry) {
      console.error(
        `Infinite Scroll: No cache entry for ${this.nextPageUrl}. Halting.`,
      );
      this._teardown();
      this._hideLoading();
      return;
    }

    if (cacheEntry.error) {
      console.error(`Infinite Scroll: Failed to load ${this.nextPageUrl}.`);
      this._hideLoading();
      // Optional: you could attempt to retry here. For now, we just stop.
      this._teardown();
      return;
    }

    // 1. Render content (using DocumentFragment for single reflow)
    if (cacheEntry.content && cacheEntry.content.children.length > 0) {
      const fragment = document.createDocumentFragment();
      fragment.append(...cacheEntry.content.children);
      this.productGrid.appendChild(fragment); // Single DOM insertion

      this.dispatchEvent(
        new CustomEvent('infiniteScroll:loaded', {
          detail: { newUrl: this.nextPageUrl },
        }),
      );
    }

    // 2. Update the URL in the address bar.
    history.replaceState({ path: this.nextPageUrl }, '', this.nextPageUrl);
    if (this.canonical_link) {
      this.canonical_link.href = this.nextPageUrl;
    }

    // 3. Update the pagination element on the page with the new one.
    if (cacheEntry.pagination && this.pagination) {
      this.pagination.innerHTML = cacheEntry.pagination.innerHTML;
    }

    // 4. Update the component's state for the *next* iteration.
    this.nextPageUrl = cacheEntry.nextUrl;

    // 5. Check if we're done. If not, pre-fetch the *next* page.
    if (!this.nextPageUrl) {
      this._teardown();
    } else {
      this._prefetchNextPage(this.nextPageUrl);
    }

    this._hideLoading();
  }
  // --- Cleanup ---

  _teardown() {
    this.state.hasNextPage = false;
    this.style.display = 'none'; // Hide the sentinel
    // Optional: Disconnect the observer if you saved a reference to it
    // this.observer.disconnect();
  }
}

customElements.define('infinite-scroll-sections', InfiniteScrollSections);
